import { useEffect, useRef, type RefObject } from "react";
import type { ViewerSession } from "../webrtc/viewerSession";
import type { CursorState } from "../webrtc/tileCompositor";

function mouseButton(button: number): "left" | "right" | "middle" | undefined {
  if (button === 0) return "left";
  if (button === 1) return "middle";
  if (button === 2) return "right";
  return undefined;
}

export function DesktopSurface(props: {
  sessionRef: RefObject<ViewerSession | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  showVideo: boolean;
  cursor: CursorState | null;
  inputEnabled: boolean;
}) {
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !props.inputEnabled) return;

    const session = () => props.sessionRef.current;

    const onMove = (e: PointerEvent) => {
      const s = session();
      if (!s) return;
      const { x, y } = s.mapClientToFramebuffer(e.clientX, e.clientY, stage);
      s.sendMouse({ action: "move", x, y });
    };
    const onDown = (e: PointerEvent) => {
      stage.setPointerCapture(e.pointerId);
      stage.focus();
      const s = session();
      if (!s) return;
      const { x, y } = s.mapClientToFramebuffer(e.clientX, e.clientY, stage);
      s.sendMouse({ action: "down", x, y, button: mouseButton(e.button) });
    };
    const onUp = (e: PointerEvent) => {
      const s = session();
      if (!s) return;
      const { x, y } = s.mapClientToFramebuffer(e.clientX, e.clientY, stage);
      s.sendMouse({ action: "up", x, y, button: mouseButton(e.button) });
    };
    const onDbl = (e: MouseEvent) => {
      const s = session();
      if (!s) return;
      const { x, y } = s.mapClientToFramebuffer(e.clientX, e.clientY, stage);
      s.sendMouse({ action: "dbl", x, y, button: mouseButton(e.button) });
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = session();
      if (!s) return;
      const { x, y } = s.mapClientToFramebuffer(e.clientX, e.clientY, stage);
      s.sendWheel({ x, y, deltaX: e.deltaX, deltaY: e.deltaY });
    };
    const onKey = (e: KeyboardEvent) => {
      if (document.activeElement !== stage) return;
      e.preventDefault();
      const s = session();
      if (!s) return;
      s.sendKey({
        action: e.type === "keydown" ? "down" : "up",
        keyCode: e.keyCode || e.code.charCodeAt(0),
        text: e.key.length === 1 ? e.key : undefined,
        modifiers: {
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        },
      });
    };

    stage.addEventListener("pointermove", onMove);
    stage.addEventListener("pointerdown", onDown);
    stage.addEventListener("pointerup", onUp);
    stage.addEventListener("dblclick", onDbl);
    stage.addEventListener("wheel", onWheel, { passive: false });
    stage.addEventListener("keydown", onKey);
    stage.addEventListener("keyup", onKey);

    return () => {
      stage.removeEventListener("pointermove", onMove);
      stage.removeEventListener("pointerdown", onDown);
      stage.removeEventListener("pointerup", onUp);
      stage.removeEventListener("dblclick", onDbl);
      stage.removeEventListener("wheel", onWheel);
      stage.removeEventListener("keydown", onKey);
      stage.removeEventListener("keyup", onKey);
    };
  }, [props.inputEnabled, props.sessionRef]);

  const cursor = props.cursor;
  const cursorStyle =
    cursor && cursor.visible
      ? {
          transform: `translate(${cursor.x - cursor.hotspotX}px, ${cursor.y - cursor.hotspotY}px)`,
          opacity: 1,
        }
      : { opacity: 0 };

  return (
    <div
      ref={stageRef}
      className="desktop-stage"
      tabIndex={0}
      aria-label="Remote desktop surface"
    >
      <canvas
        ref={props.canvasRef}
        className="desktop-canvas"
        style={{ display: props.showVideo ? "none" : "block" }}
      />
      <video
        ref={props.videoRef}
        className="desktop-video"
        playsInline
        muted
        autoPlay
        style={{ display: props.showVideo ? "block" : "none" }}
      />
      {!props.showVideo ? (
        <div className="cursor-overlay" style={cursorStyle} aria-hidden />
      ) : null}
    </div>
  );
}
